import { useState, useCallback, useMemo } from "react";

import { ClickableElement } from "@/components/ClickableElement";
import { CreateWorkflowFromRapidModal } from "@/components/flow/shared/Deploy/CreateWorkflowFromRapidModal";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "@/contexts/FlowContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSignInAndCreateWorkflow } from "@/hooks/useSignInAndCreateWorkflow";
import { useFlowState } from "@/hooks/useFlowState";
import { useReviewBatchCount } from "@/hooks/useReviewBatchCount";
import { useImproveModel } from "@/hooks/useImproveModel";
import { useContinuousImprovementEnabled } from "@/hooks/useContinuousImprovementEnabled";
import { getImproveButtonProps } from "@/hooks/improveButtonProps";
import {
    useCurrentWorkflow,
    useCurrentWorkspace,
    usePredictionsForFileAndFrameSegment,
    useIsTraining
} from "@/store";
import { setCachedInferenceResults } from "@/utils/api/datasets";
import { DeploySteps, Flows } from "@/types/flows";
import { RapidDeployModal } from "@/components/flow/shared/Deploy/RapidDeployModal";

export const IntegrateSideMenu = () => {
    const { task } = useFlow();
    const { track } = useAnalytics();
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const { isAdmin, user, requireLogin } = useAuth();
    const { setStep } = useFlowState();
    const { signInAndCreateWorkflow, loading: signInLoading } = useSignInAndCreateWorkflow();
    const workflow = useCurrentWorkflow();
    const workspace = useCurrentWorkspace();
    const predictions = usePredictionsForFileAndFrameSegment();
    const { count: reviewBatchCount, newDataReady } = useReviewBatchCount();
    const { loading: improveLoading } = useImproveModel();
    const isContinuousImprovementEnabled = useContinuousImprovementEnabled();
    const isTraining = useIsTraining();

    const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);

    const highlightImproveButton = !isTraining && (reviewBatchCount ?? 0) >= 10;

    const handleIntegrateClick = useCallback(() => {
        setIsDeployModalOpen(true);
        track({ event: AnalyticsFlowEvent.DATA_MODE_SELECTED, data: { task, dataMode: "hosted" } });
    }, [track, task]);

    const handleImproveClick = useCallback(() => {
        if (workflow && workspace) {
            if (workflow.url && Object.keys(predictions).length > 0) {
                setCachedInferenceResults(workflow.url, { predictions, annotations: {} });
            }

            setStep({
                flow: Flows.DEPLOY,
                step: DeploySteps.IMPROVE,
                workspaceUrl: workspace.url,
                workflowUrl: workflow.url
            });
        } else {
            signInAndCreateWorkflow();
        }
    }, [workflow, workspace, predictions, setStep, signInAndCreateWorkflow]);

    const isImproveDisabled = improveLoading || (signInLoading && !workflow);
    const improveProps = useMemo(
        () =>
            getImproveButtonProps(isTraining, improveLoading, {
                willPublish: false,
                iconStyle: "fad"
            }),
        [isTraining, improveLoading]
    );

    const handleDeployModalClose = useCallback(() => setIsDeployModalOpen(false), []);

    return (
        <div className="content flex flex-col justify-between">
            <div className="flex flex-col gap-4">
                <ClickableElement
                    icon="fad fa-code"
                    title="Deploy Model"
                    description="Use your model on video or images."
                    onClick={handleIntegrateClick}
                    isSelected={false}
                />
                {isAdmin && !isContinuousImprovementEnabled && (
                    <ClickableElement
                        icon="fad fa-sparkles"
                        title="Improve My Model"
                        description="Retrain your model on new data."
                        onClick={handleImproveClick}
                        isSelected={highlightImproveButton}
                        className={highlightImproveButton ? "bg-white hover:bg-tint" : undefined}
                        chip={highlightImproveButton ? "Recommended" : ""}
                        disabled={signInLoading}
                    />
                )}
                {isContinuousImprovementEnabled && (
                    <ClickableElement
                        icon={improveProps.icon}
                        title={improveProps.title}
                        description={improveProps.description}
                        onClick={handleImproveClick}
                        isSelected={newDataReady}
                        className={newDataReady ? "bg-white hover:bg-tint" : undefined}
                        chip={newDataReady ? "New Data Ready" : ""}
                        disabled={isImproveDisabled}
                    />
                )}
                <ClickableElement
                    icon="fad fa-network-wired"
                    title="Add Intelligence & Automation"
                    description="Add complex logic to your model with workflows."
                    onClick={() => {
                        track({
                            event: AnalyticsFlowEvent.OPEN_WORKFLOWS_BUTTON_CLICKED,
                            data: {}
                        });
                        if (!user) {
                            requireLogin(() => setIsTemplateModalOpen(true));
                            return;
                        }
                        setIsTemplateModalOpen(true);
                    }}
                    isSelected={false}
                />

                <CreateWorkflowFromRapidModal
                    isOpen={isTemplateModalOpen}
                    onClose={() => setIsTemplateModalOpen(false)}
                />
            </div>

            <RapidDeployModal isOpen={isDeployModalOpen} onClose={handleDeployModalClose} />
        </div>
    );
};
